import { useAction, useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Tooltip } from '@blade/ui-kit'
import { userAtom } from '@root/entities/auth'
import { UserAvatar } from '@root/entities/auth/ui'
import { BackOfficeCommentDeleteButton } from '@root/entities/back-office/ui/back-office-comment-delete-button'
import { UserShort } from '@root/entities/user-short'
import { UserShortAvatar } from '@root/entities/user-short/ui/user-short-avatar'
import { ProfileLink } from '@root/features/profile'
import { DATE_FORMAT, useFormatDuration } from '@root/shared'

import { CommentItemHeaderAuthor } from './comment-item-header-author'
import { COMMENT_ITEM_AVATAR_SIZE } from './consts'

import type { Comment } from '@root/entities/comment'
import type { CommentsModel } from '@root/features/comment/atoms'

type CommentItemHeaderProps = {
  comment: Comment.Comment
  model: CommentsModel
}

export const CommentItemHeader = ({
  comment,
  model,
}: CommentItemHeaderProps) => {
  const { t } = useTranslation()
  const [user] = useAtom(userAtom)
  const { formatDuration } = useFormatDuration()
  const deleteCommentLocally = useAction(model.deleteCommentLocallyAction)

  const isAuthor = comment.author.delugeId === user?.delugeId
  const showName = isAuthor
    ? UserShort.someName(user)
    : UserShort.someName(comment.author)

  const dateTimeAgoFormatted = useMemo(() => {
    const createdAtDiff = DateF.differenceInSeconds(
      DateF.parseISO(comment.createdAt),
      new Date(),
    )

    if (createdAtDiff < 0) {
      const timeAgo = formatDuration({
        startAt: DateF.parseISO(comment.createdAt),
        mode: 'singleUnit',
        withAgo: true,
      })

      return timeAgo
    }

    return t('common.now')
  }, [comment.createdAt, formatDuration, t])

  return (
    <Root>
      <Blank>
        <ProfileLink delugeId={comment.author.delugeId}>
          {isAuthor ? (
            <UserAvatar size={COMMENT_ITEM_AVATAR_SIZE} />
          ) : (
            <UserShortAvatar
              size={COMMENT_ITEM_AVATAR_SIZE}
              userShort={comment.author}
              outlineWidth={1}
              outlineSpacingWidth={1}
            />
          )}
        </ProfileLink>
      </Blank>
      <Heading>
        <Summary>
          <ProfileLink delugeId={comment.author.delugeId}>
            <Username>
              {showName}
              {comment.author.referralCodeStatus === 'Hyped' ? (
                <UserShortAvatar.HypedLetter />
              ) : undefined}
            </Username>
          </ProfileLink>

          <Tooltip
            content={
              <>
                <span>
                  {DateF.format(comment.createdAt, DATE_FORMAT.HH_mm_ss)}
                </span>{' '}
                {DateF.format(comment.createdAt, 'dd/MM/yy')}
              </>
            }
          >
            <DateTime>{dateTimeAgoFormatted}</DateTime>
          </Tooltip>
        </Summary>
        <Actions>
          {comment.totalReplies ? (
            <Replies>
              {t('comment.replies', { count: comment.totalReplies })}&nbsp;
              {comment.totalReplies > 0
                ? `(${comment.totalReplies}) `
                : undefined}
            </Replies>
          ) : undefined}
          <BackOfficeCommentDeleteButton
            commentId={comment.id}
            onDone={deleteCommentLocally}
          />
          {isAuthor ? (
            <CommentItemHeaderAuthor comment={comment} model={model} />
          ) : undefined}
        </Actions>
      </Heading>
    </Root>
  )
}

const Root = styled.header`
  display: flex;
  align-items: center;

  gap: 10px;
`

const Blank = styled.aside`
  width: ${COMMENT_ITEM_AVATAR_SIZE}px;
`

const Heading = styled.div`
  border-bottom: 1px solid var(--background-3);

  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 24px;
`

const Username = styled.h6`
  color: var(--text-12);

  font-size: 12px;
  font-style: normal;
  font-weight: 500;

  display: grid;
  grid-auto-flow: column;
  gap: 4px;

  svg {
    width: 16px;
  }
`

const Summary = styled.span`
  color: var(--text-8);

  font-style: normal;
  display: grid;
  grid-auto-flow: column;
  align-items: center;

  gap: 14px;
`

const Actions = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 6px;
`

const DateTime = styled.span`
  color: var(--text-8);

  display: grid;
  grid-auto-flow: column;
  gap: 4px;

  font-size: 11px;
  font-weight: 400;

  text-transform: lowercase;
`

const Replies = styled.span`
  color: var(--text-8);

  font-size: 11px;
  font-weight: 400;
`
